// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { Transition } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useDeclineInvite } from '../../api/use-decline-invite';
import { BrandedLayout } from '../../components/layouts/branded-layout';
import { PatchInviteForm } from '../../components/patch-invite-form';
import { SubmitButton } from '../../components/submit-button';
import { InfoBlock } from '../../components/ui/info-block';
import { MuxVideo } from '../../components/ui/mux-video';
import { Select } from '../../components/ui/select';
import { useEnrichedInvite } from '../../hooks/use-enriched-invite';
import { cleanPhone } from '../../utils/phone';

const resolver = zodResolver(
  z.object({
    declineReason: z.string().min(1),
  }),
);

const acceptInviteSchema = z.object({
  acceptedInvite: z.boolean().refine((x) => x === true),
});

function extractDefaults() {
  return { acceptedInvite: true };
}

function transformValues() {
  return { acceptedInvite: true };
}

export function Start({ next }) {
  const { t } = useTranslation(['page.start', 'page.specialist']);
  const invite = useEnrichedInvite();
  const declineInvite = useDeclineInvite();
  const form = useForm({ defaultValues: { declineReason: '' }, resolver });
  const procedurePlaybackId = useInviteVideoPlaybackId(invite);

  const declineOptions = useMemo(
    () => [
      { value: 'didnt-know-referred', label: t('select.didntKnowReferred') },
      { value: 'had-elsewhere', label: t('select.hadElsewhere') },
      { value: 'cant-afford', label: t('select.cantAfford') },
      { value: 'bad-timing', label: t('select.badTiming') },
      { value: 'not-necessary', label: t('select.notNecessary') },
    ],
    [t],
  );

  const onSubmit = useCallback(
    async (formFields) => {
      await declineInvite.execute(formFields);
    },
    [declineInvite],
  );

  return (
    <BrandedLayout>
      <h1 className="title">
        <Trans
          i18nKey="title"
          ns="page.start"
          values={invite}
          components={{
            br: <br />,
          }}
        />
      </h1>
      <Trans
        i18nKey={invite.practitioners.length > 1 ? 'textNoSpecialist' : 'text'}
        t={t}
        values={invite}
        components={{
          p: <p className="mb-5" />,
          strong: <strong className="font-semibold" />,
        }}
      />
      <InfoBlock>
        <Trans
          i18nKey="info"
          t={t}
          components={{
            p: <p />,
            h3: <h3 className="subtitle mb-0" />,
          }}
        />
        <MuxVideo playbackId={procedurePlaybackId} caption={t('videoCaption')} />
      </InfoBlock>
      <InfoBlock>
        <Trans
          i18nKey="bookText"
          t={t}
          components={{
            ul: <ul className="mb-4" />,
            li: <li className="mt-0.5" />,
            h3: <h3 className="subtitle mb-0" />,
            strong: <strong className="font-semibold" />,
          }}
        />

        <PatchInviteForm
          extractDefaults={extractDefaults}
          transformValues={transformValues}
          schema={acceptInviteSchema}
          nextPath={next.path}
        >
          <SubmitButton label={t('bookButtonLabel')} />
        </PatchInviteForm>
      </InfoBlock>
      <InfoBlock>
        <Trans
          i18nKey="declineText"
          t={t}
          components={{
            h3: <h3 className="subtitle mb-0" />,
            p: <p />,
          }}
        />
      </InfoBlock>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="pt-2.5 space-y-4">
          <Select
            name="declineReason"
            options={declineOptions}
            ns="page.start"
            placement="top"
            placeholder={t('select.placeholder')}
          />

          <Transition
            show={form.formState.isValid}
            enter="transition-opacity duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <SubmitButton label={t('declineButtonLabel')} variant="outlined" />
          </Transition>
        </form>
      </FormProvider>

      <InfoBlock>
        <Trans
          i18nKey="helpMessage"
          t={t}
          values={{ phone: brand.phone }}
          components={{
            h3: <h3 className="subtitle mb-0" />,
            p: <p />,
            br: <br />,
            strong: <strong className="font-semibold" />,
            a: <a href={`tel:${cleanPhone(brand.phone)}`} />,
          }}
        />
      </InfoBlock>
    </BrandedLayout>
  );
}

function useInviteVideoPlaybackId(invite) {
  const { t } = useTranslation('page.start');
  const procedurePackageGroupCode = invite.procedure?.groupCode ?? 'unknown';
  return useMemo(
    () => t([`videos.${procedurePackageGroupCode.toLowerCase()}`, 'videos.fallback']),
    [procedurePackageGroupCode, t],
  );
}
